import { Box, Divider, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { size } from 'lodash';
import { useRouter } from 'next/router';
import clientApi from '~/clientApi';
import Button from '~/components/Button';
import Modal from '~/components/Modal/Modal';
import { StepperForm } from '~/components/StepperForm';
import { routes } from '~/core/router';
import { MEDIA_PACK_ENTITY_QUERY_KEY } from '~/queries/keys';
import { CreatorType, DefaultMediaPackType } from '~/types';
import { useBreakpointValue } from '~/utils-ts';

import { STEPS } from './MediaPackDetailFirstTimeModal.constants';
import { MediaPackDetailFirstTimeModalConfirmExit } from './components';

interface Props {
  creator: CreatorType;
  isOpen: boolean;
  mediaPack: DefaultMediaPackType;
  onClose: () => void;
  creatorSlugParam?: string;
}

export const MediaPackDetailFirstTimeModal: React.FC<Props> = ({
  creator,
  isOpen,
  mediaPack,
  onClose,
  creatorSlugParam,
}) => {
  const queryClient = useQueryClient();
  const {
    isOpen: isConfirmModalOpen,
    onOpen: onConfirmModalOpen,
    onClose: onConfirmModalClose,
  } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const router = useRouter();

  const handleConfirmModalConfirmation = async () => {
    onConfirmModalClose();
    onClose();
    // If creatorSlugParam available, means we're in creator-namespaced route for Managers
    if (creatorSlugParam) {
      // And redirect accordingly
      await router.push(`/t/${creatorSlugParam}/media-packs/${mediaPack.id}`);
    } else {
      // Otherwise, the user is a creator
      await router.replace(routes.mediaPackDetail(mediaPack.id));
    }
    /**
     * TODO: This solution is not ideal as it can be triggered with no changes
     * at all, but fixes a issue where we save a step and quit the stepper and
     * the changes are not applied to the MP, if we invalidate on the
     * handleSaveAndContinue we force a rerender currently.
     */
    queryClient.invalidateQueries({
      queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY],
    });
  };
  const handleFirstTimeModalClose = () => onConfirmModalOpen();

  const handleSaveAndContinue = async (params: Partial<DefaultMediaPackType>) =>
    clientApi.mediaPacks.update({
      id: mediaPack.id,
      ...params,
      _impersonateOnce: creator?.id,
    });

  const handleSubmit = async () => {
    queryClient.invalidateQueries({
      queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY],
    });

    // If creatorSlugParam available, means we're in creator-namespaced route for Managers
    if (creatorSlugParam) {
      // And redirect accordingly
      await router.push(`/t/${creatorSlugParam}/media-packs/${mediaPack.id}`);
    } else {
      // Otherwise, the user is a creator
      await router.replace(routes.mediaPackDetail(mediaPack.id));
    }

    onClose();
  };

  const steps = STEPS({ creator, mediaPack, onSubmit: handleSaveAndContinue });

  return (
    <>
      <Modal
        isFullScreenOnMobile
        isOpen={isOpen}
        onClose={handleFirstTimeModalClose}
        size="2xl"
      >
        <Modal.Header marginBottom={{ base: 2, md: 4 }}>
          <Text fontSize="xl" fontWeight={500} marginBottom={4}>
            Personalize your Media Pack
          </Text>
          <Divider width="auto" />
        </Modal.Header>

        <Modal.CloseButton />

        <StepperForm
          flexProps={{ paddingX: 3, marginBottom: 6 }}
          hideSubmit
          isLinear
          onSubmit={handleSubmit}
          paddingX={3}
          steps={steps ?? []}
        >
          {({ activeStepIndex, formikState, isLastStep }) => {
            const activeStep = steps[activeStepIndex];
            const errors = activeStep.onValidate({
              ...activeStep.initialValues,
              ...(formikState.values ?? {}),
            });
            const isDisabled = !!size(formikState.errors) || !!size(errors);

            return (
              <Modal.Footer showDivider={!isMobile} paddingY={3} paddingX={6}>
                <Box flex={1} marginX={-4}>
                  <Flex justifyContent="space-between">
                    <Button onClick={handleFirstTimeModalClose} variant="link">
                      Cancel
                    </Button>

                    <Button
                      _disabled={{ opacity: 0.2 }}
                      colorScheme={isDisabled ? 'primary' : 'secondary'}
                      isDisabled={isDisabled}
                      isLoading={formikState.isSubmitting}
                      onClick={formikState.submitForm}
                      type="submit"
                    >
                      {isLastStep ? 'Create Media Pack' : 'Save & Continue'}
                    </Button>
                  </Flex>
                </Box>
              </Modal.Footer>
            );
          }}
        </StepperForm>
      </Modal>

      <MediaPackDetailFirstTimeModalConfirmExit
        isOpen={isConfirmModalOpen}
        onClose={onConfirmModalClose}
        onConfirm={handleConfirmModalConfirmation}
      />
    </>
  );
};
